/**
 * Custom Product Form Element
 * Handles product form submission, manages errors, and updates the cart UI.
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Cache form and relevant elements
        this.form = this.querySelector('form');
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]:not(.sticky-atc)[data-oos="0"]');
        this.submitButtonSticky = this.querySelector('[type="submit"].sticky-atc[data-oos="0"]');

        // Enable the hidden variant ID input
        this.form.querySelector('[name=id]').disabled = false;

        // Attach event listener for form submission
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Set initial state for submit buttons
        if (this.submitButton) this.submitButton.disabled = false;
        if (this.submitButtonSticky) this.submitButtonSticky.disabled = false;

        // If a cart drawer exists, set ARIA attribute
        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }
      }

      /**
       * Handles the form submission event.
       * Prevents default behavior, validates the form, and sends an AJAX request to add the product to the cart.
       * @param {Event} evt - The submit event.
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        // Prevent submission if any button is disabled
        if (
          this.submitButton?.getAttribute('aria-disabled') === 'true' ||
          this.submitButtonSticky?.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage(); // Clear any existing error messages

        // Show loading state for both buttons and their spinners
        this.toggleLoadingState(true);

        // Prepare request configuration
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Collect form data
        const formData = new FormData(this.form);
        config.body = formData;

        // Send AJAX request to add the product to the cart
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Handle server-side errors
              this.handleErrorMessage(response.description);

              // Display sold-out message if applicable
              const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton?.setAttribute('aria-disabled', true);
                this.submitButton?.querySelector('span')?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }
              return;
            } else if (this.dataset.cartType == 'page') {
              window.location = window.routes.cart_url;
              return;
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            // Reset button state and hide spinner
            this.toggleLoadingState(false);

            // Ensure cart UI reflects the updated state
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }

      /**
       * Toggles the loading state for submit buttons and spinners.
       * @param {boolean} isLoading - Whether to enable or disable the loading state.
       */
      toggleLoadingState(isLoading) {
        const buttons = [this.submitButton, this.submitButtonSticky];

        buttons.forEach((button) => {
          if (!button) return;

          button.setAttribute('aria-disabled', isLoading);
          button.classList.toggle('loading', isLoading);

          const spinner = button.parentElement?.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.toggle('hidden', !isLoading);
        });
      }

      /**
       * Displays or hides the error message element based on the provided message.
       * @param {string|boolean} errorMessage - The error message to display, or false to hide the error message.
       */
      handleErrorMessage(errorMessage = false) {
        // Lazy load error message elements
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        // Toggle visibility of the error message wrapper
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          // Update error message content
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
